import { useState } from 'react';
import { Tabs, Tab, Stack, TextField, MenuItem, InputAdornment, Chip } from '@mui/material';
import { QrCodeScanner } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { TableToolbar } from '../../components/TableToolbar';
import { StatusBadge } from '../../components/StatusBadge';
import { inventoryApi, type AssetSerial } from '../../api/inventory.api';

const SERIAL_STATUSES = ['in_stock', 'in_transit', 'installed', 'returned', 'scrapped'];
const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;

export function AssetTrackingPage() {
  const [tab, setTab] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [status, setStatus] = useState('');
  const [scan, setScan] = useState('');

  const assets = useQuery({ queryKey: ['inventory', 'assets', page, limit, status, scan], queryFn: () => inventoryApi.assets({ page, limit, status: status || undefined, search: scan || undefined }), enabled: tab === 0 });
  const ageing = useQuery({ queryKey: ['inventory', 'ageing'], queryFn: () => inventoryApi.ageing(), enabled: tab === 1 });
  const consumption = useQuery({ queryKey: ['inventory', 'consumption'], queryFn: () => inventoryApi.consumption(), enabled: tab === 2 });

  const assetCols: Column<AssetSerial>[] = [
    { key: 'serialNo', label: 'Serial No' },
    { key: 'itemId', label: 'Item', render: (r) => `#${r.itemId}` },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'warehouseId', label: 'Location', render: (r) => (r.warehouseId ? `WH #${r.warehouseId}` : '—') },
    { key: 'site', label: 'Installed at', render: (r) => (r.siteId ? `site#${r.siteId}` : r.omsId ? `oms#${r.omsId}` : '—') },
    { key: 'workOrderId', label: 'WO', render: (r) => (r.workOrderId ? `#${r.workOrderId}` : '—') },
  ];

  return (
    <>
      <PageHeader title="Asset Tracking & Reports" subtitle="Serialised asset lifecycle, inventory ageing and material consumption." />
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Assets" /><Tab label="Inventory Ageing" /><Tab label="Consumption" />
      </Tabs>

      {tab === 0 && (
        <>
          <TableToolbar
            search={scan} onSearch={(v) => { setScan(v); setPage(1); }} searchPlaceholder="Scan / enter serial number…"
            filters={
              <Stack direction="row" spacing={1} alignItems="center">
                <QrCodeScanner color="action" />
                <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ width: 180 }}
                  InputProps={{ startAdornment: <InputAdornment position="start" /> }}>
                  <MenuItem value="">All</MenuItem>
                  {SERIAL_STATUSES.map((s) => <MenuItem key={s} value={s}>{s.replace(/_/g, ' ')}</MenuItem>)}
                </TextField>
              </Stack>
            }
          />
          <DataTable columns={assetCols} rows={assets.data?.data ?? []} loading={assets.isLoading} page={page} limit={limit} total={assets.data?.meta.total ?? 0} onPageChange={setPage} onLimitChange={setLimit} rowKey={(r) => r.id} emptyTitle="No serialised assets" />
        </>
      )}

      {tab === 1 && (
        <DataTable
          columns={[
            { key: 'code', label: 'Code', width: 110 },
            { key: 'name', label: 'Item' },
            { key: 'onHand', label: 'On hand', align: 'right', render: (r) => `${r.onHand} ${r.uom}` },
            { key: 'lastMovement', label: 'Last movement', render: (r) => r.lastMovement ?? '—' },
            { key: 'ageingBucket', label: 'Ageing (days)', render: (r) => <Chip size="small" label={r.ageingBucket} color={r.ageingBucket === '180+' ? 'error' : r.ageingBucket === '91-180' ? 'warning' : 'success'} variant="outlined" /> },
          ] as Column<{ itemId: number; code: string; name: string; uom: string; onHand: number; lastMovement: string | null; ageingBucket: string }>[]}
          rows={ageing.data ?? []} loading={ageing.isLoading} page={1} limit={ageing.data?.length || 25} total={ageing.data?.length ?? 0} onPageChange={() => undefined} onLimitChange={() => undefined} rowKey={(r) => r.itemId} emptyTitle="No stock on hand"
        />
      )}

      {tab === 2 && (
        <DataTable
          columns={[
            { key: 'code', label: 'Code', width: 110 },
            { key: 'name', label: 'Item' },
            { key: 'quantity', label: 'Consumed Qty', align: 'right', render: (r) => `${r.quantity} ${r.uom ?? ''}` },
            { key: 'value', label: 'Value', align: 'right', render: (r) => inr(r.value) },
          ] as Column<{ itemId: number; code: string | null; name: string | null; uom: string | null; quantity: number; value: number }>[]}
          rows={consumption.data ?? []} loading={consumption.isLoading} page={1} limit={consumption.data?.length || 25} total={consumption.data?.length ?? 0} onPageChange={() => undefined} onLimitChange={() => undefined} rowKey={(r) => r.itemId} emptyTitle="No consumption recorded"
        />
      )}
    </>
  );
}
